import { t } from '@lingui/core/macro';
import dayjs from 'dayjs';
import { type Point, type PointerEventObject } from 'highcharts';
import { useMemo } from 'react';

import { useTrafficVisitTrendByVendor } from '@/api/common';
import { type OpportunityType } from '@/api/opportunity';
import Flare, { Area, Axis, type ChartCallbackFunction, Legend, Tooltip } from '@/components/Flare';
import { Marker, type MarkerDataType, Notes } from '@/components/Flare/annotation';
import { NotesPanel } from '@/components/panels';
import {
  DateISOFormat,
  MonthDateFormat,
  MonthDateShortFormat,
  MonthDateYearFormat,
} from '@/constants/formats';
import TrendSummaryColumn from '@/features/TrendSummaryColumn';
import { numberFormat } from '@/helper/numberFormatter';
import { getOpportunityDateRange } from '@/helper/opportunity-date-util';

import OpportunityDetailMarkerTooltip from './OpportunityDetailMarkerTooltip';

type Props = {
  opportunity?: OpportunityType;
  marginLeft: number;
  marginRight: number;
  onHoverChart?: (event: PointerEventObject, point: Point) => void;
  onHoverLeaveChart?: (event: MouseEvent) => void;
  onPostRenderChart?: ChartCallbackFunction;
};

const OpportunityDetailTrendChart = ({
  opportunity,
  marginLeft,
  marginRight,
  onHoverChart,
  onHoverLeaveChart,
  onPostRenderChart,
}: Props) => {
  const { startDate, endDate } = getOpportunityDateRange(opportunity);

  const {
    visitTrend: data,
    isLoading,
    error,
  } = useTrafficVisitTrendByVendor(startDate, endDate, { opportunityId: opportunity?.id });

  const chartData = useMemo(() => {
    if (!data || !opportunity) {
      return undefined;
    }

    return data?.map((series) => ({
      id: series.vendor.id,
      name: `${series.vendor.name} - ${series.vendor.channel!.name}`,
      custom: { seriesTitle: series.vendor.name, seriesSubtitle: series.vendor.channel!.name },
      data: series.data,
    }));
  }, [data, opportunity]);

  const markerData = useMemo<MarkerDataType[]>(
    () =>
      opportunity?.status === 'PIPELINE'
        ? [
            {
              id: 'open',
              label: t`Opened`,
              align: 'center',
              date: dayjs(opportunity?.openDate).utc().startOf('w').toISOString() ?? '',
            },
          ]
        : [
            {
              id: 'open',
              label: t`Opened`,
              align: 'center',
              date: dayjs(opportunity?.openDate).utc().startOf('w').toISOString() ?? '',
            },
            {
              id: 'close',
              label: t`Closed`,
              align: 'right',
              date: dayjs(opportunity?.closeDate).utc().startOf('w').toISOString() ?? '',
            },
          ],
    [opportunity],
  );

  const xAxisRange = useMemo(() => {
    const firstSeries = data?.[0];
    if (firstSeries == null || firstSeries.data.length === 0) {
      return undefined;
    }
    return {
      startDate: dayjs(firstSeries.data[0].sundayOfWeek),
      endDate: dayjs(firstSeries.data[firstSeries.data.length - 1].sundayOfWeek),
    };
  }, [data]);

  return (
    <NotesPanel
      startDate={xAxisRange?.startDate && xAxisRange.startDate.format(DateISOFormat)}
      endDate={xAxisRange?.endDate && xAxisRange.endDate.format(DateISOFormat)}
      title={t`Visit Trends`}
      verifyC99Tag
      noPadding
      style={{ height: 625 }}
    >
      <Flare
        key={opportunity?.id}
        data={chartData}
        colors={data?.map((series) => series.vendor.color!) ?? []}
        description={t`A trend chart showing a running total of visits by channel stacked on top of each other for the opportunity`}
        isLoading={isLoading || !opportunity}
        error={error}
        marginTop={60}
        chartMarginLeft={marginLeft}
        chartMarginRight={marginRight}
        onHoverChart={onHoverChart}
        onHoverLeaveChart={onHoverLeaveChart}
        postRender={onPostRenderChart}
      >
        <Legend />
        <Area x="sundayOfWeek" y="runningTotalVisits" position="stack" />
        <Marker
          data={markerData}
          renderLabel={(marker: MarkerDataType) => (
            <OpportunityDetailMarkerTooltip data={marker} opportunity={opportunity} />
          )}
        />
        <Axis
          position="left"
          labelFormat={(item) => numberFormat(item.value, { compact: true })}
          tickPixelInterval={125}
        />
        <Axis
          type="datetime"
          position="bottom"
          crosshair="line"
          tickPixelInterval={100}
          labelFormat={(item) => dayjs(item.value).utc().format(MonthDateShortFormat)}
          min={xAxisRange?.startDate && +xAxisRange.startDate}
          max={xAxisRange?.endDate && +xAxisRange.endDate}
        />
        <Tooltip
          shared
          titleFormat={(point) => {
            const rangeEndDate = dayjs(point?.x).utc().add(6, 'd');
            return t`${xAxisRange?.startDate?.utc().format(MonthDateFormat) ?? ''} - ${rangeEndDate.format(MonthDateYearFormat)} Visits`;
          }}
          rowValueFormat={(point) => numberFormat(point.y)}
          showTotalRow
        />
        <Notes />
        <TrendSummaryColumn width={marginRight} />
      </Flare>
    </NotesPanel>
  );
};

export default OpportunityDetailTrendChart;
